import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DataTable from "react-data-table-component";

import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import Footer from "../../common/Footer";
import PageHeader from "../PageHeader";
import Loader from "../../common/Loader";
import { triggerAlert } from "../../utils";
import {
    AdminAgentSubscriptions,
    AdminAgentInvoices,
    AdminPaymentAttempts,
    AdminOrganizations,
} from "../../utils/APIClient";

/**
 * Billing / Subscription Detail (:subId)
 *
 * Observe-only view of a single AgentSubscription. Composes the admin
 * agent-subscription retrieve endpoint with the org-scoped events endpoint
 * and the subscription-scoped payment-attempts / invoices lists. Everything
 * is read-only; only Cancel writes, and it routes through the existing
 * AgentSubscriptionAdminViewSet.cancel action.
 */

const CATEGORY_LABELS = {
    avatar: "Avatar",
    calling_v2: "Calling",
    workflow: "Workflow",
    phone_number: "Phone Numbers",
};

const fmtMoney = (n, cur = "USD") => {
    const num = Number(n || 0);
    return `${cur === "USD" ? "$" : cur + " "}${num.toFixed(2)}`;
};
const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};
const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const StatusPill = ({ status }) => {
    const c = {
        succeeded: "success", active: "success", paid: "success",
        failed: "danger", past_due: "danger",
        cancelled: "secondary",
        pending: "warning", processing: "info",
        draft: "secondary",
    }[status] || "secondary";
    return (
        <span className={`badge bg-${c} text-uppercase`} style={{ fontSize: "0.7rem" }}>
            {status || "—"}
        </span>
    );
};

const tableStyles = {
    headRow: { style: { backgroundColor: "#064c77", minHeight: "44px" } },
    headCells: {
        style: {
            color: "#ffffff", fontSize: "12px",
            fontWeight: "700", textTransform: "uppercase",
        },
    },
    rows: { style: { minHeight: "48px" } },
    cells: { style: { fontSize: "13px" } },
};

function AgentSubscriptionDetail() {
    const { subId } = useParams();
    const navigate = useNavigate();

    const [sub, setSub] = useState(null);
    const [subLoading, setSubLoading] = useState(true);

    const [events, setEvents] = useState([]);
    const [attempts, setAttempts] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [subLoadingAux, setSubLoadingAux] = useState(false);

    const [cancelling, setCancelling] = useState(false);
    const [showCancel, setShowCancel] = useState(false);
    const [cancelReason, setCancelReason] = useState("");

    document.title = sub
        ? `Digital Employees - ${sub.agent_name || "Subscription"}`
        : "Digital Employees - Subscription";

    const loadAll = useCallback(async () => {
        setSubLoading(true);
        try {
            const r = await AdminAgentSubscriptions.get(subId);
            const data = r?.data;
            setSub(data);

            // Ancillary lists — org-scoped events, sub-scoped attempts + invoices.
            setSubLoadingAux(true);
            const orgId = data?.organization;
            const promises = [
                AdminPaymentAttempts.list({ subscription_id: subId, page_size: 100 }),
                AdminAgentInvoices.list({ subscription_id: subId, page_size: 100 }),
            ];
            if (orgId) {
                promises.push(AdminOrganizations.events(orgId, {
                    subscription_id: subId, page_size: 100,
                }));
            }
            const [paResp, invResp, evResp] = await Promise.all(promises);
            setAttempts(paResp?.data?.results || []);
            setInvoices(invResp?.data?.results || []);
            setEvents(evResp?.data?.events || []);
        } catch (err) {
            triggerAlert(
                "error", "Oops...",
                err?.response?.status === 404
                    ? "Subscription not found."
                    : "Could not load subscription detail."
            );
        } finally {
            setSubLoading(false);
            setSubLoadingAux(false);
        }
    }, [subId]);

    useEffect(() => { loadAll(); }, [loadAll]);

    const handleCancel = async () => {
        setCancelling(true);
        try {
            await AdminAgentSubscriptions.cancel(subId, { reason: cancelReason.trim() });
            triggerAlert("success", "Success", "Subscription cancelled.");
            setShowCancel(false);
            setCancelReason("");
            loadAll();
        } catch (err) {
            const msg = err?.response?.data?.error || "Could not cancel subscription.";
            triggerAlert("error", "Oops...", msg);
        } finally {
            setCancelling(false);
        }
    };

    const paColumns = useMemo(() => ([
        { name: "When", cell: (r) => fmtDateTime(r.attempted_at), width: "180px" },
        { name: "Amount", cell: (r) => fmtMoney(r.amount, r.currency), width: "110px" },
        { name: "Status", cell: (r) => <StatusPill status={r.status} />, width: "110px" },
        {
            name: "Auth.net Txn ID",
            cell: (r) => (
                <span style={{ fontFamily: "monospace", fontSize: "11px" }}>
                    {r.gateway_transaction_id || "—"}
                </span>
            ),
            width: "160px",
        },
        { name: "Invoice #", cell: (r) => r.invoice_number || "—", width: "160px" },
        {
            name: "Failure",
            cell: (r) => r.failure_reason || r.failure_message || "—",
            grow: 2,
        },
    ]), []);

    const invColumns = useMemo(() => ([
        {
            name: "Invoice #",
            cell: (r) => (
                <span style={{ fontFamily: "monospace" }}>{r.invoice_number}</span>
            ),
            width: "180px",
        },
        {
            name: "Total",
            cell: (r) => fmtMoney(r.total, r.currency),
            width: "110px",
        },
        { name: "Status", cell: (r) => <StatusPill status={r.status} />, width: "100px" },
        { name: "Issued", cell: (r) => fmtDate(r.created_at), width: "120px" },
        {
            name: "Actions",
            cell: (r) => (
                <a
                    className="btn btn-sm btn-outline-primary"
                    href={`/api/admin/agent-invoices/${r.id}/pdf/`}
                    target="_blank"
                    rel="noreferrer"
                >
                    PDF
                </a>
            ),
            width: "100px",
        },
    ]), []);

    const eventColumns = useMemo(() => ([
        { name: "When", cell: (r) => fmtDateTime(r.created_at), width: "180px" },
        {
            name: "Event",
            cell: (r) => (
                <span className="badge bg-info text-uppercase">{r.event_type}</span>
            ),
            width: "160px",
        },
        { name: "Actor", cell: (r) => r.actor_email || "system", width: "220px" },
        {
            name: "Metadata",
            cell: (r) => (
                <code style={{ fontSize: "11px" }}>
                    {r.metadata && Object.keys(r.metadata).length > 0
                        ? JSON.stringify(r.metadata).slice(0, 80)
                        : ""}
                </code>
            ),
            grow: 2,
        },
    ]), []);

    return (
        <>
            <div id="layout-wrapper">
                <Header />
                <Sidebar />
                <div className="main-content">
                    <div className="page-content" style={{ backgroundColor: "#ededed" }}>
                        <div className="container-fluid">
                            <PageHeader
                                mainHeading="Subscription Detail"
                                heading="Billing / Subscriptions / Detail"
                            />

                            {subLoading && !sub && (
                                <div className="loader-overlay text-white">
                                    <Loader />
                                </div>
                            )}

                            <div className="d-flex align-items-center mb-3">
                                <button
                                    className="btn btn-outline-secondary btn-sm me-3"
                                    onClick={() => navigate("/billing/subscriptions")}
                                >
                                    ← Back to Subscriptions
                                </button>
                            </div>

                            {sub && (
                                <>
                                    <div className="card mb-3">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <h4 className="mb-1">{sub.agent_name || sub.agent_key || "—"}</h4>
                                                    <div className="text-muted">
                                                        <StatusPill status={sub.status} />
                                                        <span className="ms-2">
                                                            {CATEGORY_LABELS[sub.category] || sub.category}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="text-end">
                                                    <div className="text-muted small">Recurring Price</div>
                                                    <div className="fs-4 fw-bold">
                                                        {fmtMoney(sub.price)}
                                                    </div>
                                                </div>
                                            </div>

                                            <hr />

                                            <div className="row g-3">
                                                <div className="col-md-3">
                                                    <div className="text-muted small">Organization</div>
                                                    <div>
                                                        {sub.organization ? (
                                                            <Link to={`/customers/customerslist/${sub.organization}`}>
                                                                {sub.organization_name || sub.organization}
                                                            </Link>
                                                        ) : "—"}
                                                    </div>
                                                </div>
                                                <div className="col-md-3">
                                                    <div className="text-muted small">Packs</div>
                                                    <div>{sub.packs ?? "—"}</div>
                                                </div>
                                                <div className="col-md-3">
                                                    <div className="text-muted small">Started</div>
                                                    <div>{fmtDate(sub.created_at)}</div>
                                                </div>
                                                <div className="col-md-3">
                                                    <div className="text-muted small">Next Renewal</div>
                                                    <div>{fmtDate(sub.billing_date)}</div>
                                                </div>
                                                {sub.minutes != null && (
                                                    <div className="col-md-3">
                                                        <div className="text-muted small">Minutes</div>
                                                        <div>
                                                            {sub.used_minutes ?? 0} / {sub.minutes}
                                                        </div>
                                                    </div>
                                                )}
                                                {sub.units != null && (
                                                    <div className="col-md-3">
                                                        <div className="text-muted small">Units</div>
                                                        <div>
                                                            {sub.used_units ?? 0} / {sub.units}
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="col-md-3">
                                                    <div className="text-muted small">ARB Subscription ID</div>
                                                    <div style={{ fontFamily: "monospace", fontSize: "12px" }}>
                                                        {sub.arb_subscription_id || "—"}
                                                    </div>
                                                </div>
                                                {sub.cancelled_at && (
                                                    <div className="col-md-3">
                                                        <div className="text-muted small">Cancelled</div>
                                                        <div>{fmtDate(sub.cancelled_at)}</div>
                                                    </div>
                                                )}
                                            </div>

                                            {sub.status !== "cancelled" && (
                                                <div className="mt-3">
                                                    <button
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => setShowCancel(true)}
                                                    >
                                                        Cancel Subscription
                                                    </button>
                                                </div>
                                            )}
                                            {sub.cancellation_reason && (
                                                <div className="alert alert-secondary mt-3 mb-0 py-2">
                                                    <strong>Cancellation reason:</strong> {sub.cancellation_reason}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="card mb-3">
                                        <div className="card-header bg-white">
                                            <h6 className="mb-0">Payment Attempts</h6>
                                        </div>
                                        <div className="card-body">
                                            {subLoadingAux ? (
                                                <Loader />
                                            ) : attempts.length === 0 ? (
                                                <div className="text-muted">No payment attempts.</div>
                                            ) : (
                                                <DataTable
                                                    columns={paColumns}
                                                    data={attempts}
                                                    pagination
                                                    paginationPerPage={10}
                                                    customStyles={tableStyles}
                                                />
                                            )}
                                        </div>
                                    </div>

                                    <div className="card mb-3">
                                        <div className="card-header bg-white">
                                            <h6 className="mb-0">Invoices</h6>
                                        </div>
                                        <div className="card-body">
                                            {subLoadingAux ? (
                                                <Loader />
                                            ) : invoices.length === 0 ? (
                                                <div className="text-muted">No invoices yet.</div>
                                            ) : (
                                                <DataTable
                                                    columns={invColumns}
                                                    data={invoices}
                                                    pagination
                                                    paginationPerPage={10}
                                                    customStyles={tableStyles}
                                                />
                                            )}
                                        </div>
                                    </div>

                                    <div className="card mb-3">
                                        <div className="card-header bg-white">
                                            <h6 className="mb-0">Events</h6>
                                        </div>
                                        <div className="card-body">
                                            {subLoadingAux ? (
                                                <Loader />
                                            ) : events.length === 0 ? (
                                                <div className="text-muted">No events recorded.</div>
                                            ) : (
                                                <DataTable
                                                    columns={eventColumns}
                                                    data={events}
                                                    pagination
                                                    paginationPerPage={10}
                                                    customStyles={tableStyles}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    <Footer />
                </div>
            </div>

            {showCancel && (
                <div
                    className="modal fade show d-block"
                    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                    onClick={() => !cancelling && setShowCancel(false)}
                >
                    <div
                        className="modal-dialog modal-dialog-centered"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Cancel Subscription</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => !cancelling && setShowCancel(false)}
                                    disabled={cancelling}
                                />
                            </div>
                            <div className="modal-body">
                                <p>
                                    This ends the recurring ARB at Authorize.net and marks the
                                    subscription as cancelled. This action cannot be undone.
                                </p>
                                <div className="mb-3">
                                    <label className="form-label">Reason (optional)</label>
                                    <textarea
                                        className="form-control"
                                        rows={2}
                                        maxLength={255}
                                        value={cancelReason}
                                        onChange={(e) => setCancelReason(e.target.value)}
                                        disabled={cancelling}
                                        placeholder="e.g. Customer requested via support ticket #123"
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowCancel(false)}
                                    disabled={cancelling}
                                >
                                    Keep Active
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleCancel}
                                    disabled={cancelling}
                                >
                                    {cancelling ? "Cancelling…" : "Cancel Subscription"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default AgentSubscriptionDetail;
